import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { apiFetch, UPLOAD_API_TIMEOUT_MS } from "@/lib/apiFetch";
import { apiConfig } from "@/config/api";

interface NriClaimDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentNri?: string;
  callsign?: string;
  onSuccess: () => void;
}

export function NriClaimDialog({
  open,
  onOpenChange,
  currentNri,
  callsign,
  onSuccess,
}: NriClaimDialogProps) {
  const [nriLama, setNriLama] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [evidenceFile, setEvidenceFile] = React.useState<File | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [nriError, setNriError] = React.useState("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!open) {
      setNriLama("");
      setNotes("");
      setEvidenceFile(null);
      setNriError("");
    }
  }, [open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
      if (!validTypes.includes(file.type)) {
        toast.error("Format file tidak valid. Gunakan JPEG, PNG, WebP, atau PDF.");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Ukuran file maksimal 10MB.");
        return;
      }
      setEvidenceFile(file);
    }
  };

  const removeFile = () => {
    setEvidenceFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    setNriError("");

    if (!nriLama.trim()) {
      setNriError("NRI Lama wajib diisi");
      return;
    }

    if (!/^\d+$/.test(nriLama.trim())) {
      setNriError("NRI harus berupa angka");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("nri_lama", nriLama.trim());
      formData.append("notes", notes.trim());
      if (evidenceFile) {
        formData.append("evidence", evidenceFile);
      }

      const res = await apiFetch(`${apiConfig.baseURL}/api/nri-requests`, {
        method: "POST",
        body: formData,
        timeoutMs: UPLOAD_API_TIMEOUT_MS,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Gagal mengajukan klaim NRI");
      }

      toast.success("Pengajuan klaim NRI berhasil dikirim");
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Gagal mengajukan klaim NRI");
    } finally {
      setSubmitting(false);
    }
  };

  const hasCurrentNri = currentNri && !/0000$/.test(currentNri);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Klaim NRI
          </DialogTitle>
          <DialogDescription>
            {hasCurrentNri
              ? `NRI Anda saat ini (${currentNri}) tidak sesuai. Ajukan perubahan dengan mengisi NRI lama Anda dan lampirkan bukti.`
              : "Anda belum memiliki NRI. Ajukan klaim dengan mengisi NRI lama Anda dan lampirkan bukti."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {hasCurrentNri && (
            <div className="bg-muted/50 rounded-lg p-3 text-sm">
              <span className="text-muted-foreground">NRI Saat Ini: </span>
              <span className="font-mono font-medium">{currentNri}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="nri_lama">
              NRI Lama <span className="text-destructive">*</span>
            </Label>
            <Input
              id="nri_lama"
              placeholder="Masukkan NRI lama Anda (angka)"
              value={nriLama}
              onChange={(e) => {
                setNriLama(e.target.value.replace(/\D/g, ""));
                setNriError("");
              }}
              maxLength={20}
              inputMode="numeric"
              className={nriError ? "border-destructive" : ""}
            />
            {nriError && <p className="text-xs text-destructive">{nriError}</p>}
          </div>

          <div className="space-y-2">
            <Label>Bukti</Label>
            {evidenceFile ? (
              <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm truncate flex-1">{evidenceFile.name}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={removeFile}
                  type="button"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center gap-2 p-6 rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-muted-foreground/50 cursor-pointer transition-colors"
              >
                <Upload className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Upload bukti NRI lama (JPEG, PNG, WebP, PDF)
                </p>
                <p className="text-xs text-muted-foreground/60">Maksimal 10MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Keterangan</Label>
            <Textarea
              id="notes"
              placeholder="Ceritakan kronologi atau informasi tambahan (opsional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Batal
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Mengirim...
              </>
            ) : (
              "Ajukan Klaim"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
